/**
 * ESTABILIZACIÓN POST-F13 — LOS CAMBIOS DE UN HISTORIAL, EN CRISTIANO
 *
 * Los eventos de auditoría guardan `cambios` como JSON con tres formas:
 *   · instantánea plana        {"alta":true,"minimo":10,"maximo":100,…}
 *   · antes / después           {"antes":false,"despues":true}  o  {"antes":{…},"despues":{…}}
 *   · campo a campo             {"descripcion":{"antes":"A","despues":"B"}}
 * Enseñarlo crudo ({"alta":true,…}) no sirve a nadie. Esto lo convierte en líneas
 * «Máximo: 100 → 30», «Inventariable: Sí», con etiquetas en castellano, Sí/No, fechas
 * legibles y sin identificadores internos (uuid). Si se pasa la instantánea anterior
 * de la misma entidad (`previo`), una instantánea plana se enseña como diferencia.
 * El JSON sigue disponible, plegado, en «Detalles técnicos».
 */
import type { ReactNode } from 'react';

/** Etiquetas de los campos que aparecen en los historiales. Lo que no esté, se humaniza (puntoPedido → Punto pedido). */
const ETIQUETAS: Readonly<Record<string, string>> = {
  minimo: 'Mínimo', maximo: 'Máximo', puntoPedido: 'Punto de pedido', inventariable: 'Inventariable', activo: 'Activo',
  descripcion: 'Descripción', codigo: 'Código', valor: 'Valor', tipo: 'Tipo', motivo: 'Motivo', cantidad: 'Cantidad',
  sentido: 'Sentido', documento: 'Documento', estado: 'Estado', lotes: 'Controla lotes', series: 'Controla series',
  caducidad: 'Controla caducidad', factor: 'Factor', observaciones: 'Observaciones', nombre: 'Nombre', fecha: 'Fecha',
  fechaCaducidad: 'Caducidad', fechaFabricacion: 'Fabricación', proveedor: 'Proveedor', numero: 'Número', alcance: 'Alcance',
  ubicacion: 'Ubicación', permiteNegativo: 'Permite stock negativo', origen: 'Origen', antes: 'Antes', despues: 'Después',
  stockMinimo: 'Mínimo', stockMaximo: 'Máximo', unidad: 'Unidad', almacen: 'Almacén', conteo: 'Conteo', diferencia: 'Diferencia',
};
/** Claves que no son un dato para la persona (marcas del propio evento). */
const OCULTAS = new Set(['alta']);
/** Orden de lectura: el del diccionario (Mínimo, Máximo, Punto de pedido…), no el que deja jsonb; lo desconocido, al final. */
const ORDEN = Object.keys(ETIQUETAS);
const porOrden = (a: string, b: string): number => {
  const [x, y] = [ORDEN.indexOf(a), ORDEN.indexOf(b)];
  return (x === -1 ? ORDEN.length : x) - (y === -1 ? ORDEN.length : y) || a.localeCompare(b);
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ISO = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

type Json = null | boolean | number | string | Json[] | { [k: string]: Json };
const esObjeto = (v: unknown): v is Record<string, Json> => typeof v === 'object' && v !== null && !Array.isArray(v);
const esAntesDespues = (v: unknown): v is { antes: Json; despues: Json } => esObjeto(v) && 'antes' in v && 'despues' in v && Object.keys(v).length === 2;

export function etiquetaCampo(k: string): string {
  if (ETIQUETAS[k] !== undefined) return ETIQUETAS[k];
  const t = k.replace(/^id(?=[A-Z])/, '').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Un valor suelto, legible. Los identificadores internos no se enseñan (están en «Detalles técnicos»). */
export function valorLegible(v: Json | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (typeof v === 'number') return v.toLocaleString('es-ES');
  if (typeof v === 'string') {
    if (UUID.test(v)) return '(cambiado)';
    if (ISO.test(v)) {
      const d = new Date(v.length === 10 ? `${v}T00:00:00` : v);
      if (!Number.isNaN(d.getTime())) return v.length === 10 ? d.toLocaleDateString('es-ES') : d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
    }
    return v;
  }
  if (Array.isArray(v)) return v.map((x) => valorLegible(x)).join(', ');
  return Object.entries(v).filter(([k, x]) => !OCULTAS.has(k) && !(typeof x === 'string' && UUID.test(x))).map(([k, x]) => `${etiquetaCampo(k)}: ${valorLegible(x)}`).join(' · ');
}

interface Linea { readonly campo: string; readonly antes?: string; readonly despues: string }

/** Las líneas legibles de un `cambios`. */
export function lineasCambios(cambios: unknown, previo?: unknown): Linea[] {
  if (!esObjeto(cambios)) return cambios === null || cambios === undefined ? [] : [{ campo: '', despues: valorLegible(cambios as Json) }];
  //  {"antes": …, "despues": …}
  if (esAntesDespues(cambios)) {
    const { antes, despues } = cambios;
    if (esObjeto(antes) && esObjeto(despues)) {
      return [...new Set([...Object.keys(antes), ...Object.keys(despues)])].sort(porOrden)
        .filter((k) => JSON.stringify(antes[k]) !== JSON.stringify(despues[k]))
        .map((k) => ({ campo: etiquetaCampo(k), antes: valorLegible(antes[k]), despues: valorLegible(despues[k]) }));
    }
    return [{ campo: '', antes: valorLegible(antes), despues: valorLegible(despues) }];
  }
  const lineas: Linea[] = [];
  const anterior = esObjeto(previo) ? previo : null;
  for (const [k, v] of Object.entries(cambios).sort(([a], [b]) => porOrden(a, b))) {
    if (OCULTAS.has(k)) continue;
    if (esAntesDespues(v)) {
      lineas.push({ campo: etiquetaCampo(k), antes: valorLegible(v.antes), despues: valorLegible(v.despues) });
    } else if (anterior !== null) {
      //  Con la instantánea anterior, sólo lo que cambió, como «antes → después».
      if (JSON.stringify(anterior[k]) !== JSON.stringify(v)) lineas.push({ campo: etiquetaCampo(k), antes: valorLegible(anterior[k]), despues: valorLegible(v) });
    } else if (!(typeof v === 'string' && UUID.test(v))) {
      lineas.push({ campo: etiquetaCampo(k), despues: valorLegible(v) });
    }
  }
  return lineas;
}

/**
 * El bloque de cambios de un evento: líneas legibles + «Detalles técnicos» plegado.
 * `previo`: la instantánea anterior de la misma entidad (p. ej. la configuración anterior del artículo en ESE almacén).
 */
export function CambiosEvento({ cambios, previo }: { cambios: unknown; previo?: unknown }): ReactNode {
  if (cambios === null || cambios === undefined) return null;
  const lineas = lineasCambios(cambios, previo);
  return (
    <div className="pequena cambios-evento">
      {lineas.length === 0 ? <div className="tenue">Sin cambios visibles.</div> : (
        <ul className="cambios-evento-lista">
          {lineas.map((l, i) => (
            <li key={i}>
              {l.campo !== '' && <span>{l.campo}: </span>}
              {l.antes !== undefined && <><span className="tenue">{l.antes}</span> → </>}
              <strong>{l.despues}</strong>
            </li>
          ))}
        </ul>
      )}
      <details className="detalles-tecnicos">
        <summary className="tenue">Detalles técnicos</summary>
        <pre className="mono">{JSON.stringify(cambios, null, 2)}</pre>
      </details>
    </div>
  );
}
